import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Volume2, VolumeX, Heart } from "lucide-react";

const VIDEO_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_saloni-bday-surprise/artifacts/jdbt4b6w_WhatsApp%20Video%202026-08-06%20at%2012.56.40%20AM.mp4";

export default function VideoWish() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <section id="video-wish" data-testid="video-wish-section" className="relative py-24 md:py-32 overflow-hidden">
      {/* soft ambient background */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[80%] h-[60%] rounded-full opacity-60"
             style={{ background: "radial-gradient(closest-side, rgba(255,214,231,0.35), rgba(167,199,231,0.25), transparent)" }} />
      </div>

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-5"
        >
          <p className="font-body text-xs uppercase tracking-[0.35em] text-[#7B9ACC]">Chapter two & a half</p>
          <h2 className="mt-3 font-display font-black text-[#0B192C] text-5xl sm:text-6xl leading-[0.95] tracking-tight">
            A message
            <br />
            <span className="italic">from Via.</span>
          </h2>
          <p className="mt-6 font-body text-[#334E68] text-base sm:text-lg max-w-md">
            He can't spell "birthday" yet. He can't say "Happy Birthday, Mumma" without a five-second buffer.
            But he tried. And that, honestly, is the whole point.
          </p>
          <div className="mt-6 flex items-center gap-2 font-hand text-[#7B9ACC] text-2xl">
            <Heart className="w-5 h-5 fill-[#FFD6E7] text-[#FFD6E7]" />
            press play, cry a little
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.95 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-7"
        >
          <div className="relative mx-auto max-w-[420px]">
            {/* Phone frame */}
            <div className="relative rounded-[2.75rem] p-3 bg-gradient-to-b from-[#0B192C] to-[#050914] shadow-[0_30px_80px_-20px_rgba(11,25,44,0.4)]">
              <div className="absolute top-1 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 bg-black/80 rounded-full px-3 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-white/60" />
                <span className="text-[10px] text-white/80 font-body tracking-widest uppercase">Via cam</span>
              </div>
              <div
                onClick={togglePlay}
                className="relative aspect-[9/16] rounded-[2rem] overflow-hidden bg-black cursor-pointer group"
                data-testid="video-wish-player"
              >
                <video
                  ref={videoRef}
                  playsInline
                  preload="metadata"
                  onPlay={() => { setPlaying(true); setLoadError(false); }}
                  onPause={() => setPlaying(false)}
                  onEnded={() => setPlaying(false)}
                  onError={() => setLoadError(true)}
                  className="absolute inset-0 w-full h-full object-cover"
                >
                  <source src={VIDEO_URL} type="video/mp4" />
                </video>
                {/* Play overlay */}
                {!playing && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/70 via-black/25 to-transparent"
                  >
                    <motion.div
                      whileHover={{ scale: 1.08 }}
                      whileTap={{ scale: 0.95 }}
                      className="relative w-20 h-20 rounded-full bg-white/95 backdrop-blur flex items-center justify-center"
                    >
                      <span className="absolute inset-0 rounded-full border-2 border-white/60 animate-ping" />
                      <Play className="w-8 h-8 text-[#0B192C] fill-[#0B192C] translate-x-0.5" />
                    </motion.div>
                  </motion.div>
                )}

                {/* Mute toggle */}
                {playing && (
                  <button
                    onClick={toggleMute}
                    className="absolute bottom-4 right-4 z-10 w-10 h-10 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white/90 hover:bg-black/80 transition-colors"
                    aria-label={muted ? "Unmute" : "Mute"}
                    data-testid="video-wish-mute"
                  >
                    {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                )}

                {/* Load error fallback */}
                {loadError && (
                  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-[#0B192C]/95 px-6 text-center">
                    <p className="font-hand text-white text-xl">Video couldn't load 🥲</p>
                    <p className="mt-2 font-body text-white/70 text-xs">Tap to retry — or open on your phone browser.</p>
                    <a
                      href={VIDEO_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="mt-4 rounded-full bg-white text-[#0B192C] px-4 py-2 text-xs font-body"
                    >
                      Open video ↗
                    </a>
                  </div>
                )}

                {/* Caption on top */}
                {!playing && (
                  <div className="absolute top-6 left-4 right-4 z-10">
                    <p className="font-hand text-white text-2xl leading-tight drop-shadow-lg">
                      "Happy birt-...
                      <br />... mumma."
                    </p>
                    <p className="mt-1 font-body text-white/70 text-[11px] tracking-[0.3em] uppercase">
                      Via, aged 1 yr 9 mo
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Floating sticker */}
            <motion.div
              initial={{ rotate: -8, opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.7 }}
              className="absolute -left-4 -bottom-4 rounded-full bg-white px-4 py-2 border border-[#EAF0F6] shadow-md rotate-[-8deg] font-hand text-[#7B9ACC] text-xl"
            >
              exclusive premiere 🎂
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
